import classNamesStyle from 'classnames';
import {
  forwardRef,
  MutableRefObject,
  ReactNode,
  Ref,
  TextareaHTMLAttributes,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { cn } from '../../../utils/cn';

type UseAutosizeTextAreaProps = {
  textAreaRef: MutableRefObject<HTMLTextAreaElement | null>;
  minHeight?: number;
  maxHeight?: number;
  triggerAutoSize: string;
};

export const useAutosizeTextArea = ({
  textAreaRef,
  triggerAutoSize,
  maxHeight = Number.MAX_SAFE_INTEGER,
  minHeight = 0,
}: UseAutosizeTextAreaProps) => {
  useEffect(() => {
    const offsetBorder = 2;
    const textAreaElement = textAreaRef.current;
    if (!textAreaElement) return;

    // Always apply constraints; they may change over time
    textAreaElement.style.minHeight = `${minHeight + offsetBorder}px`;
    if (maxHeight > minHeight + offsetBorder) {
      textAreaElement.style.maxHeight = `${maxHeight}px`;
    } else {
      textAreaElement.style.maxHeight = '';
    }

    // Reset then size to content up to the cap
    textAreaElement.style.height = `${minHeight + offsetBorder}px`;
    const scrollHeight = textAreaElement.scrollHeight;
    const nextHeight =
      scrollHeight + offsetBorder > maxHeight ? maxHeight : scrollHeight + offsetBorder;
    textAreaElement.style.height = `${nextHeight}px`;
  }, [textAreaRef.current, triggerAutoSize, maxHeight, minHeight]);
};

type AutosizeTextAreaProps = {
  maxHeight?: number;
  minHeight?: number;
  error?: boolean;
  endIcon?: ReactNode;
} & TextareaHTMLAttributes<HTMLTextAreaElement>;

export const AutosizeTextarea = forwardRef<HTMLTextAreaElement, AutosizeTextAreaProps>(
  (
    {
      maxHeight = Number.MAX_SAFE_INTEGER,
      minHeight = 20,
      className,
      onChange,
      value,
      endIcon,
      error,
      ...props
    }: AutosizeTextAreaProps,
    ref: Ref<HTMLTextAreaElement>,
  ) => {
    const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
    const [triggerAutoSize, setTriggerAutoSize] = useState('');
    const [effectiveMaxHeight, setEffectiveMaxHeight] = useState<number>(maxHeight);

    // If no explicit maxHeight is provided, cap to the parent container height
    useLayoutEffect(() => {
      const el = textAreaRef.current;
      if (!el) return;

      // Respect explicit maxHeight from props
      if (maxHeight !== Number.MAX_SAFE_INTEGER) {
        setEffectiveMaxHeight(maxHeight);
        return;
      }

      const compute = () => {
        const parent = el.parentElement as HTMLElement | null;
        if (!parent) return;
        const parentHeight = parent.clientHeight;
        if (parentHeight > 0) {
          setEffectiveMaxHeight(parentHeight);
        }
      };

      compute();

      const parent = el.parentElement as HTMLElement | null;
      if (!parent) return;

      const ro = new ResizeObserver(() => compute());
      ro.observe(parent);
      window.addEventListener('resize', compute);
      return () => {
        ro.disconnect();
        window.removeEventListener('resize', compute);
      };
    }, [maxHeight]);

    useAutosizeTextArea({
      textAreaRef,
      triggerAutoSize: triggerAutoSize,
      maxHeight: effectiveMaxHeight,
      minHeight,
    });

    useImperativeHandle(ref, () => ({
      textArea: textAreaRef.current as HTMLTextAreaElement,
      focus: () => textAreaRef?.current?.focus(),
      maxHeight: effectiveMaxHeight,
      minHeight,
    }));

    useEffect(() => {
      setTriggerAutoSize(value as string);
    }, [props?.defaultValue, value]);

    return (
      <textarea
        {...props}
        value={value}
        ref={textAreaRef}
        className={classNamesStyle(
          cn(
            'custom-scroll flex w-full resize-none overflow-auto rounded-xl border bg-card px-6 py-3 text-sm text-foreground placeholder:text-foreground-subtle ring-inset focus-within:!ring-[3px] focus:outline-none disabled:cursor-not-allowed disabled:opacity-50',
            className,
          ),
          { '!pr-12': endIcon },
          {
            'border-input focus-within:border-foreground focus-within:ring-ring/25': !error,
          },
          {
            'border-destructive text-destructive focus-within:!ring-destructive/20': error,
          },
        )}
        onChange={(e) => {
          setTriggerAutoSize(e.target.value);
          onChange?.(e);
        }}
      />
    );
  },
);
